// Approved nurture vocabulary seed — trait_key -> category.
// Descriptions are short behavioral glosses for LLM + UI tooltips.
// Source: NPC_Personality_Design_Spec.md section 7.
export const TRAIT_SEEDS: Array<[key: string, category: string, desc: string]> = [
  // Trust
  ['quick_to_trust', 'trust', 'Extends trust to new people with little hesitation.'],
  ['slow_to_trust', 'trust', 'Needs sustained evidence before trusting someone.'],
  ['distrusts_strangers', 'trust', 'Treats unknown people as potential threats.'],
  ['distrusts_authority', 'trust', 'Questions motives of institutional power holders.'],
  ['distrusts_wealthy_people', 'trust', 'Assumes wealthy people exploit others.'],
  ['distrusts_outsiders', 'trust', 'Withholds trust from people outside own group.'],
  ['expects_betrayal', 'trust', 'Anticipates being betrayed even without evidence.'],
  ['assumes_good_intent', 'trust', 'Defaults to believing others mean well.'],
  ['tests_people_before_trusting', 'trust', 'Probes others with small tests before opening up.'],
  ['forgives_betrayal_easily', 'trust', 'Recovers trust quickly after being betrayed.'],
  ['holds_grudges', 'trust', 'Retains resentment long after being wronged.'],
  // Attachment
  ['commitment_seeking', 'attachment', 'Actively wants stable long-term bonds.'],
  ['avoids_commitment', 'attachment', 'Dodges labels and long-term obligations.'],
  ['fears_abandonment', 'attachment', 'Expects loved ones to leave.'],
  ['fears_dependency', 'attachment', 'Dreads needing others too much.'],
  ['emotionally_guarded', 'attachment', 'Keeps feelings hidden until very safe.'],
  ['emotionally_clingy', 'attachment', 'Seeks constant closeness and reassurance.'],
  ['jealous_in_relationships', 'attachment', 'Feels threatened by rivals for affection.'],
  ['possessive_in_relationships', 'attachment', 'Wants to control partner attention and access.'],
  ['needs_frequent_reassurance', 'attachment', 'Needs repeated confirmation of being wanted.'],
  ['values_independence_in_relationships', 'attachment', 'Protects autonomy inside relationships.'],
  ['idealizes_romantic_partners', 'attachment', 'Puts partners on a pedestal early.'],
  ['expects_relationships_to_fail', 'attachment', 'Assumes romance will end badly.'],
  ['falls_in_love_easily', 'attachment', 'Develops romantic feelings fast.'],
  ['slow_to_form_romantic_attachment', 'attachment', 'Takes long to feel romantic bonds.'],
  // Sexuality
  ['sexually_reserved', 'sexuality', 'Restrained about sexual expression.'],
  ['sexually_adventurous', 'sexuality', 'Enjoys exploring sexually.'],
  ['associates_sex_with_love', 'sexuality', 'Needs love to enjoy sex.'],
  ['separates_sex_from_love', 'sexuality', 'Comfortable with sex detached from love.'],
  ['seeks_validation_through_sex', 'sexuality', 'Uses sex to feel wanted.'],
  ['uses_sex_to_create_connection', 'sexuality', 'Uses intimacy to bond.'],
  ['avoids_sex_without_emotional_trust', 'sexuality', 'Needs emotional trust first.'],
  ['comfortable_with_casual_sex', 'sexuality', 'At ease with casual encounters.'],
  ['uncomfortable_with_casual_sex', 'sexuality', 'Dislikes casual encounters.'],
  ['strongly_values_monogamy', 'sexuality', 'Deeply committed to exclusivity.'],
  ['comfortable_with_multiple_partners', 'sexuality', 'Open to multiple partners.'],
  ['ashamed_of_sexual_desire', 'sexuality', 'Feels shame about own desire.'],
  ['open_about_sexuality', 'sexuality', 'Talks about sex frankly.'],
  ['private_about_sexuality', 'sexuality', 'Keeps sexual life private.'],
  // Social
  ['fiercely_loyal', 'social', 'Stands by friends at personal cost.'],
  ['protective_of_friends', 'social', 'Defends friends readily.'],
  ['protective_of_family', 'social', 'Prioritizes family safety.'],
  ['protective_of_children', 'social', 'Shields children instinctively.'],
  ['people_pleaser', 'social', 'Sacrifices own needs to please.'],
  ['approval_seeking', 'social', 'Chases praise and validation.'],
  ['conflict_avoidant', 'social', 'Dodges confrontation.'],
  ['confrontational', 'social', 'Faces conflict head-on.'],
  ['competitive', 'social', 'Must win and compare.'],
  ['cooperative', 'social', 'Prefers teamwork over rivalry.'],
  ['status_conscious', 'social', 'Tracks hierarchy and prestige.'],
  ['suspicious_of_flattery', 'social', 'Distrusts compliments.'],
  ['comfortable_leading', 'social', 'Enjoys taking charge.'],
  ['uncomfortable_leading', 'social', 'Dislikes being in charge.'],
  ['comfortable_following', 'social', 'Happy to follow others.'],
  ['respects_strength', 'social', 'Admires toughness.'],
  ['respects_intelligence', 'social', 'Admires smarts.'],
  ['respects_kindness', 'social', 'Admires compassion.'],
  ['respects_hard_work', 'social', 'Admires effort.'],
  ['resentful_of_privilege', 'social', 'Resents unearned advantage.'],
  // Self-concept
  ['high_self_confidence', 'self', 'Believes in own abilities.'],
  ['low_self_confidence', 'self', 'Doubts own abilities.'],
  ['low_self_worth', 'self', 'Feels undeserving of good things.'],
  ['believes_self_capable', 'self', 'Trusts own competence.'],
  ['believes_self_incompetent', 'self', 'Feels incapable.'],
  ['perfectionist', 'self', 'Demands flawlessness.'],
  ['self_critical', 'self', 'Judges self harshly.'],
  ['prideful', 'self', 'Protects ego fiercely.'],
  ['humble', 'self', 'Downplays own importance.'],
  ['shame_prone', 'self', 'Feels shame easily.'],
  ['guilt_prone', 'self', 'Feels guilt easily.'],
  ['needs_to_feel_useful', 'self', 'Must feel helpful to matter.'],
  ['needs_to_feel_needed', 'self', 'Must feel depended upon.'],
  ['needs_control', 'self', 'Must steer situations.'],
  ['needs_recognition', 'self', 'Craves acknowledgment.'],
  ['comfortable_with_failure', 'self', 'Takes failure in stride.'],
  ['afraid_of_failure', 'self', 'Dreads failing.'],
  ['afraid_of_embarrassment', 'self', 'Dreads looking foolish.'],
  // Conflict/stress
  ['withdraws_under_stress', 'stress', 'Pulls away when pressured.'],
  ['becomes_aggressive_under_stress', 'stress', 'Lashes out under pressure.'],
  ['becomes_controlling_under_stress', 'stress', 'Micromanages when stressed.'],
  ['becomes_indecisive_under_stress', 'stress', 'Freezes decisions under stress.'],
  ['becomes_reckless_under_stress', 'stress', 'Acts rashly under stress.'],
  ['freezes_under_pressure', 'stress', 'Locks up in crises.'],
  ['stays_calm_in_crisis', 'stress', 'Steady when things break.'],
  ['seeks_help_under_stress', 'stress', 'Asks for help when strained.'],
  ['isolates_under_stress', 'stress', 'Goes solitary under strain.'],
  ['uses_humor_under_stress', 'stress', 'Jokes to defuse tension.'],
  ['overthinks_problems', 'stress', 'Ruminates on problems.'],
  ['avoids_problems', 'stress', 'Dodges difficult issues.'],
  ['faces_problems_directly', 'stress', 'Confronts issues promptly.'],
  ['suppresses_emotions', 'stress', 'Buries feelings.'],
  ['talks_through_emotions', 'stress', 'Processes feelings aloud.'],
  ['distracts_self_from_emotions', 'stress', 'Stays busy to avoid feeling.'],
  // Security
  ['hypervigilant', 'security', 'Constantly scans for danger.'],
  ['risk_averse', 'security', 'Avoids gambles.'],
  ['comfortable_with_risk', 'security', 'Takes gambles easily.'],
  ['resourceful', 'security', 'Makes do with little.'],
  ['hoards_resources', 'security', 'Stockpiles against scarcity.'],
  ['generous_with_resources', 'security', 'Shares freely.'],
  ['afraid_of_poverty', 'security', 'Fears destitution.'],
  ['obsessed_with_financial_security', 'security', 'Fixates on money safety.'],
  ['comfortable_with_uncertainty', 'security', 'Tolerates ambiguity.'],
  ['needs_stability', 'security', 'Requires predictable life.'],
  ['always_has_backup_plan', 'security', 'Keeps contingencies ready.'],
  ['expects_disaster', 'security', 'Braces for worst case.'],
  ['believes_things_will_work_out', 'security', 'Trusts positive outcomes.'],
  // Values
  ['family_first', 'values', 'Puts family above all.'],
  ['friends_first', 'values', 'Puts friends above all.'],
  ['self_reliance', 'values', 'Depends on self only.'],
  ['community_minded', 'values', 'Serves the group.'],
  ['freedom_above_security', 'values', 'Chooses liberty over safety.'],
  ['security_above_freedom', 'values', 'Chooses safety over liberty.'],
  ['justice_driven', 'values', 'Demands fairness.'],
  ['mercy_driven', 'values', 'Prefers compassion over punishment.'],
  ['loyalty_above_rules', 'values', 'Backs people over principles.'],
  ['rules_above_loyalty', 'values', 'Backs principles over people.'],
  ['honesty_above_harmony', 'values', 'Tells truth over keeping peace.'],
  ['harmony_above_honesty', 'values', 'Keeps peace over blunt truth.'],
  ['tradition_oriented', 'values', 'Honors custom.'],
  ['change_oriented', 'values', 'Welcomes change.'],
  ['achievement_oriented', 'values', 'Chases accomplishment.'],
  ['wealth_oriented', 'values', 'Chases money.'],
  ['status_oriented', 'values', 'Chases prestige.'],
  ['knowledge_oriented', 'values', 'Chases learning.'],
  ['pleasure_oriented', 'values', 'Chases enjoyment.'],
  ['duty_oriented', 'values', ' honors obligation.'],
  // Authority
  ['respects_authority', 'authority', 'Defers to legitimate power.'],
  ['questions_authority', 'authority', 'Probes power claims.'],
  ['resists_authority', 'authority', 'Pushes back on control.'],
  ['seeks_authority_positions', 'authority', 'Wants to be in charge.'],
  ['uncomfortable_with_authority', 'authority', 'Uneasy around power.'],
  ['obedient_to_rules', 'authority', 'Follows rules by default.'],
  ['rules_are_flexible', 'authority', 'Treats rules as guidelines.'],
  ['rebellious', 'authority', 'Breaks norms deliberately.'],
  ['institutionally_loyal', 'authority', 'Stays loyal to organizations.'],
  ['anti_institutional', 'authority', 'Distrusts organizations.'],
  // Money
  ['materialistic', 'money', 'Values possessions highly.'],
  ['nonmaterialistic', 'money', 'Dismisses possessions.'],
  ['frugal', 'money', 'Spends carefully.'],
  ['spends_impulsively', 'money', 'Buys on impulse.'],
  ['generous_with_money', 'money', 'Gives money freely.'],
  ['protective_of_possessions', 'money', 'Guards belongings.'],
  ['comfortable_sharing', 'money', 'Shares things easily.'],
  ['equates_money_with_security', 'money', 'Sees money as safety.'],
  ['equates_money_with_success', 'money', 'Sees money as achievement.'],
  ['uninterested_in_wealth', 'money', 'Ignores wealth pursuit.'],
  // Work
  ['workaholic', 'work', 'Works compulsively.'],
  ['career_driven', 'work', 'Prioritizes advancement.'],
  ['competitive_about_achievement', 'work', 'Must out-achieve peers.'],
  ['intrinsically_motivated', 'work', 'Driven from within.'],
  ['externally_motivated', 'work', 'Driven by rewards.'],
  ['avoids_responsibility', 'work', 'Dodges ownership.'],
  ['takes_responsibility_for_others', 'work', 'Carries others burdens.'],
  ['persistent_after_failure', 'work', 'Keeps going after setbacks.'],
  ['gives_up_after_failure', 'work', 'Quits after setbacks.'],
  // Morality (no good/evil score — concrete tendencies only)
  ['highly_honest', 'morality', 'Tells truth even when costly.'],
  ['comfortable_lying', 'morality', 'Lies without discomfort.'],
  ['lies_to_avoid_conflict', 'morality', 'Lies to keep peace.'],
  ['lies_for_personal_gain', 'morality', 'Lies for advantage.'],
  ['keeps_promises', 'morality', 'Honors commitments.'],
  ['breaks_promises_when_convenient', 'morality', 'Drops promises when costly.'],
  ['strong_sense_of_duty', 'morality', 'Acts from obligation.'],
  ['ends_justify_means', 'morality', 'Accepts dirty means for goals.'],
  ['values_fairness', 'morality', 'Insists on fair treatment.'],
  ['protective_of_the_weak', 'morality', 'Defends vulnerable people.'],
  ['revenge_motivated', 'morality', 'Seeks payback.'],
  ['forgiving', 'morality', 'Lets go of wrongs.'],
  ['believes_people_deserve_second_chances', 'morality', 'Gives repeats chances.'],
];

export const TRAIT_KEYS = new Set(TRAIT_SEEDS.map(([k]) => k));
